import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { CommunityComment } from "../types";

interface CommentsByPost {
  commentIds: string[];
  total: number;
  hasMore: boolean;
}

interface CommentStoreState {
  byPost: Record<string, CommentsByPost>;
  entities: Record<string, CommunityComment>;
}

interface CommentStoreActions {
  setComments: (
    postId: string,
    comments: CommunityComment[],
    total: number,
    hasMore: boolean,
  ) => void;
  appendComments: (postId: string, comments: CommunityComment[]) => void;
  addComment: (
    postId: string,
    comment: CommunityComment,
    parentId?: string,
  ) => void;
  removeComment: (postId: string, commentId: string) => void;
  updateComment: (commentId: string, patch: Partial<CommunityComment>) => void;
  /** 设置某顶级评论的全部子评论（展开加载后覆盖） */
  setReplies: (
    postId: string,
    parentId: string,
    replies: CommunityComment[],
  ) => void;
}

export const useCommentStore = create<
  CommentStoreState & CommentStoreActions
>()(
  immer((set) => ({
    byPost: {},
    entities: {},

    setComments: (postId, comments, total, hasMore) =>
      set((state) => {
        const ids: string[] = [];
        for (const c of comments) {
          state.entities[c.id] = c;
          ids.push(c.id);
          if (c.replies) {
            for (const r of c.replies) {
              state.entities[r.id] = r;
            }
          }
        }
        state.byPost[postId] = { commentIds: ids, total, hasMore };
      }),

    appendComments: (postId, comments) =>
      set((state) => {
        const bucket = state.byPost[postId];
        if (!bucket) return;
        for (const c of comments) {
          state.entities[c.id] = c;
          bucket.commentIds.push(c.id);
          if (c.replies) {
            for (const r of c.replies) {
              state.entities[r.id] = r;
            }
          }
        }
      }),

    addComment: (postId, comment, parentId?: string) =>
      set((state) => {
        const bucket = state.byPost[postId];
        if (!bucket) return;

        if (parentId) {
          // 找到被回复者的显示名，写入 parentAuthorName
          const parentComment = state.entities[parentId];
          const enriched: CommunityComment = parentComment
            ? { ...comment, parentAuthorName: parentComment.author.displayName }
            : comment;
          state.entities[enriched.id] = enriched;

          // 尝试找到顶级根评论并追加到其 replies（仅作备份）
          // CommentItem 的本地显示由 reply-event 事件总线负责
          const containsId = (
            replies: CommunityComment[] | undefined,
            targetId: string,
          ): boolean => {
            if (!replies) return false;
            return replies.some(
              (r) => r.id === targetId || containsId(r.replies, targetId),
            );
          };
          const rootId = bucket.commentIds.find((id) => {
            if (id === parentId) return true;
            const root = state.entities[id];
            return containsId(root?.replies, parentId);
          });
          if (rootId) {
            const root = state.entities[rootId];
            if (root) {
              root.replies = [...(root.replies ?? []), enriched];
            }
          }
          // 找不到根时不再降级插入顶级列表，避免重复显示
        } else {
          // 顶级评论插到最前面
          state.entities[comment.id] = comment;
          bucket.commentIds.unshift(comment.id);
          bucket.total += 1;
        }
      }),

    removeComment: (postId, commentId) =>
      set((state) => {
        delete state.entities[commentId];
        const bucket = state.byPost[postId];
        if (bucket) {
          bucket.commentIds = bucket.commentIds.filter(
            (id) => id !== commentId,
          );
          bucket.total = Math.max(0, bucket.total - 1);
        }
      }),

    updateComment: (commentId, patch) =>
      set((state) => {
        const existing = state.entities[commentId];
        if (existing) {
          Object.assign(existing, patch);
        }
      }),

    setReplies: (postId, parentId, replies) =>
      set((state) => {
        const bucket = state.byPost[postId];
        if (!bucket) return;
        // 找到顶级根评论（parentId 就是顶级评论的 id）
        const root = state.entities[parentId];
        if (root) {
          root.replies = replies;
          root.replyTotal = replies.length;
        }
      }),
  })),
);
